import { PROJECT_NOTE_CURSOR_COLOUR_PALETTE } from 'constants/color';
import { PAGE } from 'constants/general';
import { ProjectNoteSaveState } from 'enums';

export const PROJECT_NOTE = {
  AUTOSAVE_MS: 800,
  SAVED_LABEL_MS: 2000,
  EDITOR_CLASS:
    'prose dark:prose-invert max-w-none overflow-visible focus:outline-none min-h-32 md:min-h-44',
  EMBED: {
    PAGE: PAGE.FIRST,
    PAGE_SIZE: PAGE.TAKE.PROJECT_NOTE,
    TASK_IDS_SEPARATOR: ',',
    PICKER_LIST_MAX_HEIGHT_CLASS: 'max-h-64',
    ATTR: {
      TASK_LIST: 'data-task-list-embed',
      TASK_IDS: 'data-task-ids'
    }
  },
  CURSOR: {
    HASH_MODULUS: 1_000_000_007,
    COLORS: PROJECT_NOTE_CURSOR_COLOUR_PALETTE
  },
  REALTIME: {
    /** Pusher presence channel prefix (`presence-project-note-<noteId>`). */
    PRESENCE_CHANNEL_PREFIX: 'presence-project-note-',
    EVENT: {
      UPDATE: 'client-yjs-update',
      AWARENESS: 'client-yjs-awareness',
      SYNC_REQUEST: 'client-yjs-sync-request',
      SYNC_REPLY: 'client-yjs-sync-reply'
    },
    /** Wire payload field names for client-yjs events. */
    PAYLOAD: {
      STATE_VECTOR: 'sv',
      AWARENESS_UPDATE: 'u'
    },
    DEBOUNCE_MS: {
      BROADCAST: 150,
      PERSIST: 2500
    },
    CHUNK: {
      /** Base64 chars per chunk — keeps each Pusher payload under ~10 KB. */
      MAX_CHARS: 7000,
      /** `String.fromCharCode` batch size when encoding bytes → base64. */
      ENCODE_STRIDE: 0x8000,
      MIN_PARTS: 1
    }
  }
} as const;

export const PROJECT_NOTE_SAVE_LABEL_KEYS: Partial<
  Record<ProjectNoteSaveState, string>
> = {
  [ProjectNoteSaveState.SAVING]: 'project_note.saving',
  [ProjectNoteSaveState.SAVED]: 'project_note.saved'
};

export const NOTE_LIST_ITEM_STATE_CLASS = {
  active: '!bg-primary-50 dark:!bg-primary-dark-100',
  idle: ''
} as const;

export const NOTE_TITLE_INPUT_CLASS =
  'min-w-0 flex-1 cursor-text bg-transparent text-2xl font-semibold text-gray-900 outline-none border-0 border-b border-dashed border-gray-300 pb-0.5 hover:border-gray-400 focus:border-primary-400 dark:text-white dark:border-gray-600 dark:hover:border-gray-500 dark:focus:border-primary-dark-500 placeholder:text-gray-400';

export const NOTE_SAVE_STATUS_CLASS =
  'pointer-events-none absolute -bottom-5 right-0 text-xs text-secondary-900 dark:text-secondary-dark-900';

export const NOTE_GHOST = {
  FRAME_CLASS: 'pointer-events-none select-none opacity-30 dark:opacity-25',
  EDITOR_GRID_CLASS: 'grid grid-cols-1 items-start ~gap-4/6 xl:grid-cols-2',
  TITLE_CLASS:
    'h-7 w-2/3 max-w-xs border-0 border-b border-dashed border-gray-300 sm:h-8 dark:border-gray-600',
  BAR_CLASS: 'h-2.5 rounded-sm bg-gray-200 dark:bg-gray-700',
  BODY_WIDTH_CLASS: ['w-full', 'w-11/12', 'w-4/5', 'w-5/6', 'w-2/3'] as const,
  COMMENT_WIDTH_CLASS: ['w-full', 'w-3/4'] as const,
  COMMENTS_COLUMN_CLASS:
    'min-w-0 self-stretch border-gray-200 border-t ~pt-8/6 2xl:border-l 2xl:border-t-0 2xl:pl-4 2xl:pt-0 dark:border-gray-700',
  DESKTOP_PANE_CLASS:
    'relative hidden h-full min-h-80 w-full md:block md:min-h-96',
  OVERLAY_CLASS: 'absolute inset-0 z-10 flex items-center justify-center ~p-4/6'
} as const;
